import { useUserData } from '../../store/useUserData';

interface Permission {
	'@type': 'Permission';
	'@id': string;
	id: number;
	label: string;
	permissionGroup: string;
}

interface SecurityRolePermission {
	'@type': 'SecurityRolePermission';
	'@id': string;
	id: number;
	permission: Permission;
	view: boolean;
	edit: boolean;
	delete: boolean;
	execute: boolean;
}

export type PermissionAction = 'view' | 'edit' | 'delete' | 'execute';

export interface PermissionInput {
	label: string;
	action?: PermissionAction;
}

export const usePermissionCheck = () => {
	const { currentUserRole } = useUserData();

	const permissionCheck = {
		checkPermission: ({ label, action = 'view' }: PermissionInput): boolean => {
			if (!currentUserRole?.securityRolePermission) {
				return false;
			}

			const rolePermission = (
				currentUserRole.securityRolePermission as SecurityRolePermission[]
			).find((p: SecurityRolePermission) => p.permission.label === label);
			// console.log("rolePermission", rolePermission, label, action);

			if (!rolePermission) {
				return false;
			}

			return !!rolePermission[action];
		},

		hasAnyPermissionInGroup: (groupId: string, action: PermissionAction = 'view'): boolean => {
			if (!currentUserRole?.securityRolePermission) {
				return false;
			}

			const permissions = currentUserRole.securityRolePermission as SecurityRolePermission[];
			return permissions.some(
				(p: SecurityRolePermission) => 
					p.permission.permissionGroup === `/api/permission_groups/${groupId}` && 
					!!p[action]
			);
		},

		hasViewPermission: (label: string): boolean => {
			if (label === 'General') {
				// For General tab, also check if the user has any permission in group 2
				return permissionCheck.hasAnyPermissionInGroup('2');
				// return true;
			}
			return permissionCheck.checkPermission({ label, action: 'view' });
		},

		hasEditPermission: (label: string): boolean => {
			if (label === 'General') {
				return permissionCheck.hasAnyPermissionInGroup('2', 'edit');
				// return true;
			}
			return permissionCheck.checkPermission({ label, action: 'edit' });
		},

		hasDeletePermission: (label: string): boolean => {
			if (label === 'General') {
				return permissionCheck.hasAnyPermissionInGroup('2', 'delete');
				// return true;
			}
			return permissionCheck.checkPermission({ label, action: 'delete' });
		},

		hasExecutePermission: (label: string): boolean => {
			if (label === 'General') {
				return permissionCheck.hasAnyPermissionInGroup('2', 'execute');
				// return true;
			}
			return permissionCheck.checkPermission({ label, action: 'execute' });
		}
	,

		// Explicit module-level permission checks (permission group 4)
		// These check for a specific permission label within group 4 and test the relevant action flag.
		hasModuleViewPermission: (label: string): boolean => {
			// console.log('Checking module view permission for label:', label, currentUserRole?.securityRolePermission);
			if (!currentUserRole?.securityRolePermission) return false;
			const perms = currentUserRole.securityRolePermission as SecurityRolePermission[];
			const found = perms.find(
				(p) => p.permission.permissionGroup === '/api/permission_groups/4' && p.permission.label === label
			);
			// Module-level permission requires edit rights on the module permission row
			return !!found && !!found.view;
		},

		hasModuleEditPermission: (label: string): boolean => {
			if (!currentUserRole?.securityRolePermission) return false;
			const perms = currentUserRole.securityRolePermission as SecurityRolePermission[];
			const found = perms.find(
				(p) => p.permission.permissionGroup === '/api/permission_groups/4' && p.permission.label === label
			);
			return !!found && !!found.edit;
		},

		hasModuleDeletePermission: (label: string): boolean => {
			if (!currentUserRole?.securityRolePermission) return false;
			const perms = currentUserRole.securityRolePermission as SecurityRolePermission[];
			const found = perms.find(
				(p) => p.permission.permissionGroup === '/api/permission_groups/4' && p.permission.label === label
			);
			return !!found && !!found.delete;
		},

		hasModuleExecutePermission: (label: string): boolean => {
			if (!currentUserRole?.securityRolePermission) return false;
			const perms = currentUserRole.securityRolePermission as SecurityRolePermission[];
			const found = perms.find(
				(p) => p.permission.permissionGroup === '/api/permission_groups/4' && p.permission.label === label
			);
			return !!found && !!found.execute;
		}
	};

	return permissionCheck;
};

export default usePermissionCheck;
